import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  DataTable,
  EmptyState,
  Badge,
  BlockStack,
  Text,
  Select,
  TextField,
  Spinner,
  InlineGrid,
  Button,
  InlineStack,
  Modal,
  Banner,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { reviewsApi } from '../utils/api';
import { Review } from '../types';
import { isLocalDevelopment } from '../utils/env';

const Reviews: React.FC = () => {
  const { t } = useTranslation();
  const { shopId } = useAuth();
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);

  useEffect(() => {
    fetchReviews();
    checkSubscription();
  }, [shopId, filter]);

  const checkSubscription = async () => {
    if (!shopId || isLocalDevelopment()) {
      return;
    }

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
        {
          headers: {
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIsPaid(data.plan === 'paid' && data.status === 'active');
      }
    } catch (err) {
      setIsPaid(false);
    }
  };

  const fetchReviews = async () => {
    if (!shopId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Verificar se tem productId na URL
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get('productId');
    
    const filters: { status?: string; productId?: string } = {};
    if (filter !== 'all') {
      filters.status = filter;
    }
    if (productId) {
      filters.productId = productId;
    }

    try {
      const result = await reviewsApi.getAll(shopId, filters);
      
      if (result.error) {
        setFetchError(result.error);
        setReviews([]);
      } else if (result.data) {
        setReviews(result.data);
        setFetchError(null);
      } else {
        setReviews([]);
        setFetchError(null);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error fetching reviews';
      setFetchError(errorMsg);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId: string) => {
    if (!shopId) return;

    setActionLoading(reviewId);
    setMessage(null);

    const result = await reviewsApi.approve(reviewId, shopId);
    
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
    } else {
      setMessage({ type: 'success', text: t('reviews.approvedSuccess', { defaultValue: 'Review approved successfully!' }) });
      await fetchReviews();
      setShowDetailModal(false);
    }

    setActionLoading(null);
  };

  const handleReject = async (reviewId: string) => {
    if (!shopId) return;

    setActionLoading(reviewId);
    setMessage(null);

    const result = await reviewsApi.reject(reviewId, shopId);
    
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
    } else {
      setMessage({ type: 'success', text: t('reviews.rejectedSuccess', { defaultValue: 'Review rejected.' }) });
      await fetchReviews();
      setShowDetailModal(false);
    }

    setActionLoading(null);
  };

  const handleViewDetails = (review: Review) => {
    setSelectedReview(review);
    setShowDetailModal(true);
  };

  const filterOptions = [
    { label: t('reviews.filter.all'), value: 'all' },
    { label: t('reviews.filter.pending'), value: 'pending' },
    { label: t('reviews.filter.approved'), value: 'approved' },
    { label: t('reviews.filter.rejected'), value: 'rejected' },
  ];

  const getStatusBadge = (status: Review['status']) => {
    switch (status) {
      case 'approved':
        return <Badge tone="success">{t('reviews.approved')}</Badge>;
      case 'pending':
        return <Badge tone="attention">{t('reviews.pending')}</Badge>;
      case 'rejected':
        return <Badge tone="critical">{t('reviews.rejected')}</Badge>;
      default:
        return null;
    }
  };

  // Filtrar reviews por busca
  const filteredReviews = reviews.filter((review) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      review.productTitle.toLowerCase().includes(searchLower) ||
      review.customerName.toLowerCase().includes(searchLower) ||
      review.comment.toLowerCase().includes(searchLower)
    );
  });

  const rows = filteredReviews.map((review) => [
    review.productTitle,
    review.customerName,
    '⭐'.repeat(review.rating),
    review.comment.substring(0, 50) + (review.comment.length > 50 ? '...' : ''),
    new Date(review.createdAt).toLocaleDateString(),
    <InlineStack gap="200" key={review.id}>
      {getStatusBadge(review.status)}
      <Button size="micro" onClick={() => handleViewDetails(review)}>
        Ver
      </Button>
    </InlineStack>,
  ]);

  // Verificar se está filtrando por produto
  const urlParams = new URLSearchParams(window.location.search);
  const productId = urlParams.get('productId');
  const productTitle = productId && reviews.length > 0 
    ? reviews[0]?.productTitle 
    : null;

  return (
    <Page
      title={productTitle ? `${t('reviews.title', { defaultValue: 'Avaliações' })} - ${productTitle}` : t('reviews.title', { defaultValue: 'Avaliações' })}
      backAction={productId ? {
        content: t('common.back', { defaultValue: 'Voltar' }),
        onAction: () => {
          window.location.href = '/products';
        },
      } : undefined}
      secondaryActions={isPaid ? [
        {
          content: 'PRO',
        },
      ] : undefined}
    >
      {message && (
        <Banner
          tone={message.type === 'success' ? 'success' : 'critical'}
          onDismiss={() => setMessage(null)}
        >
          {message.text}
        </Banner>
      )}
      {fetchError && (
        <Banner
          tone="critical"
          onDismiss={() => setFetchError(null)}
        >
          {t('reviews.fetchError', { defaultValue: 'Error fetching reviews' })}: {fetchError}
        </Banner>
      )}
      {productId && productTitle && (
        <Banner
          tone="info"
          onDismiss={() => {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            fetchReviews();
          }}
        >
          {t('reviews.filteringByProduct', { defaultValue: 'Mostrando avaliações do produto' })}: <strong>{productTitle}</strong>
        </Banner>
      )}

      <Card>
        <BlockStack gap="400">
          <InlineGrid columns={{ xs: 1, sm: 2 }}>
            <TextField
              label={t('common.search')}
              value={search}
              onChange={setSearch}
              placeholder={t('common.search')}
              autoComplete="off"
            />
            <Select
              label={t('reviews.filter.all')}
              options={filterOptions}
              value={filter}
              onChange={setFilter}
            />
          </InlineGrid>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <BlockStack gap="300" align="center">
                <Spinner size="large" />
                <Text as="p" tone="subdued" alignment="center">
                  {t('common.loading', { defaultValue: 'Loading...' })}
                </Text>
              </BlockStack>
            </div>
          ) : filteredReviews.length === 0 ? (
            <EmptyState
              heading={t('reviews.noReviews')}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>{t('reviews.noReviewsDescription')}</p>
            </EmptyState>
          ) : (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
              headings={[
                t('reviews.fields.product'),
                t('reviews.fields.customer'),
                t('reviews.fields.rating'),
                t('reviews.fields.comment'),
                t('reviews.fields.date'),
                t('reviews.fields.status'),
              ]}
              rows={rows}
            />
          )}
        </BlockStack>
      </Card>

      {/* Modal de detalhes e moderação */}
      <Modal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedReview(null);
        }}
        title={t('reviews.details', { defaultValue: 'Review Details' })}
        primaryAction={
          selectedReview?.status === 'pending'
            ? {
                content: 'Aprovar',
                onAction: () => selectedReview && handleApprove(selectedReview.id),
                loading: actionLoading === selectedReview?.id,
              }
            : undefined
        }
        secondaryActions={
          selectedReview?.status === 'pending'
            ? [
                {
                  content: 'Rejeitar',
                  onAction: () => selectedReview && handleReject(selectedReview.id),
                  loading: actionLoading === selectedReview?.id,
                  destructive: true,
                },
              ]
            : []
        }
      >
        <Modal.Section>
          {selectedReview && (
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">
                  {t('reviews.fields.product', { defaultValue: 'Product' })}
                </Text>
                <Text as="p">{selectedReview.productTitle}</Text>
              </BlockStack>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">
                  Cliente
                </Text>
                <Text as="p">{selectedReview.customerName}</Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  {selectedReview.customerEmail}
                </Text>
                {selectedReview.verifiedPurchase && (
                  <Badge tone="success">✓ Compra verificada</Badge>
                )}
              </BlockStack>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">
                  {t('reviews.fields.rating', { defaultValue: 'Rating' })}
                </Text>
                <Text as="p">{'⭐'.repeat(selectedReview.rating)}</Text>
                {selectedReview.title && (
                  <Text as="p" variant="headingSm">
                    {selectedReview.title}
                  </Text>
                )}
                <Text as="p">{selectedReview.comment}</Text>
              </BlockStack>

              {selectedReview.photos && Array.isArray(selectedReview.photos) && selectedReview.photos.length > 0 && (
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3">
                    {t('reviews.fields.photos', { defaultValue: 'Fotos' })}
                  </Text>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 150px))',
                    gap: '12px',
                    marginTop: '8px'
                  }}>
                    {selectedReview.photos.map((photo, index) => (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          width: '150px',
                          height: '150px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #E5E7EB',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          flexShrink: 0,
                        }}
                        onClick={() => window.open(photo, '_blank')}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <img
                          src={photo}
                          alt={`Review photo ${index + 1}`}
                          style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                          draggable={false}
                        />
                      </div>
                    ))}
                  </div>
                  <Text as="p" tone="subdued" variant="bodySm">
                    {t('reviews.photosClickHint', { defaultValue: 'Clique na foto para abrir em tamanho maior' })}
                  </Text>
                </BlockStack>
              )}

              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">
                  Status
                </Text>
                {getStatusBadge(selectedReview.status)}
              </BlockStack>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">
                  Data
                </Text>
                <Text as="p" tone="subdued">
                  {new Date(selectedReview.createdAt).toLocaleString('pt-BR')}
                </Text>
              </BlockStack>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default Reviews;

